import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { NgtAvailabilityResponseYes, type AvailabilityResponse } from './isp.api';
import type { ProductWithBundleId } from '~/models/CampaignModel';

type InternetStoreData = {
  selectedAddress: InternetCustomerAddress | null;
  availableProducts?: AvailabilityResponse;
  selectedBroadBandProduct?: ProductWithBundleId;
  validAvailabilityResponse: undefined | NgtAvailabilityResponseYes;
};

type InternetStore = InternetStoreData & {
  // Actions
  setSelectedAddress: (address: InternetCustomerAddress | null) => void;
  setAvailableProducts: (products: AvailabilityResponse) => void;
  setSelectedInternetProduct: (product: ProductWithBundleId | undefined) => void;
  resetState: () => void;
};

const defaultState: InternetStoreData = {
  selectedAddress: null,
  availableProducts: undefined,
  selectedBroadBandProduct: undefined,
  validAvailabilityResponse: undefined,
};

export const useInternetFlowStore = create<InternetStore>()(
  devtools(
    persist(
      (_set) => {
        // helper for action name in devtools
        const set = (action: string, state: Partial<InternetStore>) => _set(state, undefined, action);
        return {
          ...defaultState,
          // actions
          setSelectedAddress(selectedAddress) {
            set('setSelectedAddress', { selectedAddress });
          },
          setAvailableProducts(availableProducts) {
            set('setAvailableProducts', { availableProducts });
          },
          setSelectedInternetProduct(selectedBroadBandProduct) {
            set('setSelectedInternetProduct', { selectedBroadBandProduct });
          },
          resetState() {
            set('resetState', defaultState);
          },
        };
      },
      {
        name: 'rtv-zustand-internet',
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
    {
      name: 'internet-flow-store',
      store: 'InternetFlowStore',
      anonymousActionType: 'internet-flow',
      enabled: true,
    },
  ),
);

type PartialAddress = {
  addrType: 'multiple'; // discriminated union helper, added by us when mapping response
  city: string;
  count: number;
  country: string;
  description: string;
  municipality: string;
  number: number | null;
  postalCode: string;
  street: string;
  type: string;
};
export type SingleInternetCustomerAddress = Omit<PartialAddress, 'count' | 'addrType'> & {
  addrType: 'single'; // discriminated union helper, added by us when mapping response
  count: 1;
  // only potentially available when count=1
  apartment: string;
  houseLetter?: string;
  propertyNumber?: string;
  propertyPartNumber: string;
  north: string;
  east: string;
};

export type InternetCustomerAddress = PartialAddress | SingleInternetCustomerAddress;
